import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http, formatINR, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Star, ShieldCheck, Award, Leaf, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { ProductGallery } from "@/components/ProductGallery";

const DOSHA_STYLES = {
  vata: "bg-sky-50 text-sky-800 border-sky-200",
  pitta: "bg-amber-50 text-amber-900 border-amber-200",
  kapha: "bg-emerald-50 text-emerald-900 border-emerald-200",
  tridoshic: "bg-purple-50 text-purple-900 border-purple-200",
};

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useEffect(() => {
    http.get(`/products/${id}`).then((r) => setProduct(r.data));
    http.get(`/reviews/${id}`).then((r) => setReviews(r.data));
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      const { data } = await http.post("/reviews", { product_id: id, rating, comment });
      setReviews([data, ...reviews]);
      setComment("");
      toast.success("Review posted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please login to review");
    }
  };

  if (!product) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid gap-10 lg:grid-cols-2">
        <ProductGallery product={product} />

        <div>
          <span className={`text-[10px] uppercase font-mono px-2 py-1 rounded-full border ${DOSHA_STYLES[product.dosha]}`}>
            {product.dosha}
          </span>
          <div className="text-xs font-mono text-[#5A6B5D] mt-3 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Sold by {product.vendor_name}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A201C] leading-tight mt-2">{product.name}</h1>

          <div className="mt-3 flex items-center gap-2 text-sm">
            <div className="flex items-center gap-0.5 text-[#D97706]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating || 0) ? "fill-current" : ""}`} />
              ))}
            </div>
            <span className="font-mono">{product.rating || "New"}</span>
            <span className="text-[#5A6B5D]">({product.reviews_count} reviews)</span>
          </div>

          <div className="mt-6 flex items-end gap-3">
            <div className="font-serif text-4xl font-bold text-[#1E3A2B]">{formatINR(product.price)}</div>
            {product.original_price > product.price && (
              <>
                <div className="text-[#5A6B5D] line-through">{formatINR(product.original_price)}</div>
                <div className="text-xs bg-[#D97706] text-white px-2 py-1 rounded-full font-semibold">
                  Save {Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
                </div>
              </>
            )}
          </div>

          <p className="mt-6 text-[#5A6B5D] leading-relaxed">{product.description}</p>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-4">
              <div className="text-xs font-mono uppercase text-[#5A6B5D] flex items-center gap-1"><Leaf className="w-3 h-3" /> Key Benefits</div>
              <ul className="mt-2 text-sm text-[#1A201C] space-y-1">
                {product.benefits.map((b) => <li key={b}>• {b}</li>)}
              </ul>
            </div>
            <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-4">
              <div className="text-xs font-mono uppercase text-[#5A6B5D] flex items-center gap-1"><Award className="w-3 h-3" /> Ingredients</div>
              <ul className="mt-2 text-sm text-[#1A201C] space-y-1">
                {product.ingredients.map((i) => <li key={i}>• {i}</li>)}
              </ul>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#E2DDD5] rounded-full bg-white">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
              <span className="px-3 font-mono">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => add(product, qty)} className="flex-1 btn-forest" data-testid="detail-add-to-cart">
              Add {qty} to Cart · {formatINR(product.price * qty)}
            </button>
          </div>

          <div className="mt-6 bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-xl p-4">
            <div className="text-xs font-mono uppercase text-[#1E3A2B] font-semibold">Nutritionist Note · Dr. Rupali Srivastava (Dietitian &amp; Nutritionist)</div>
            <p className="text-sm text-[#1A201C] mt-2 leading-relaxed italic">
              "Recommended for daily Rasayana routine. Best taken with warm water in the morning. Consult your physician if pregnant or on medication."
            </p>
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <div className="mt-16">
        <h2 className="font-serif text-2xl font-bold text-[#1A201C]">Customer Reviews</h2>
        {user && user !== false && user.role === "customer" && (
          <form onSubmit={submitReview} className="mt-6 bg-white border border-[#E2DDD5] rounded-2xl p-5" data-testid="review-form">
            <div className="flex items-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setRating(n)} data-testid={`review-rating-${n}`}>
                  <Star className={`w-5 h-5 ${n <= rating ? "fill-[#D97706] text-[#D97706]" : "text-[#E2DDD5]"}`} />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              className="w-full border border-[#E2DDD5] rounded-lg p-3 text-sm min-h-[80px]"
              required
              data-testid="review-comment-input"
            />
            <button className="mt-3 btn-forest text-sm" data-testid="submit-review-button">Post Review</button>
          </form>
        )}
        <div className="mt-6 space-y-4">
          {reviews.length === 0 && <p className="text-[#5A6B5D] text-sm">No reviews yet. Be the first.</p>}
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#E8F0EA] flex items-center justify-center text-[#1E3A2B] font-serif font-bold">{r.user_name?.[0]}</div>
                <div>
                  <div className="text-sm font-semibold">{r.user_name}</div>
                  <div className="flex text-[#D97706]">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? "fill-current" : ""}`} />)}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-[#1A201C]">{r.comment}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
